import { apiDelete, apiGet, apiPost } from '../mutator';
import type {
  DeviceSession,
  LoginResult,
  MeResult,
  OtpPurpose,
  OtpRequestResult,
  Paged,
  SupportAccessRow,
  VerifyOtpResult,
} from '../types';

/**
 * `/v1/auth/*` — the panel's front door.
 * Mirrors `backend/src/auth/auth.controller.ts` and `two-factor.controller.ts`.
 */

export function requestOtp(input: {
  phone: string;
  purpose?: OtpPurpose;
}): Promise<OtpRequestResult> {
  return apiPost<OtpRequestResult>('/auth/otp/request', {
    phone: input.phone,
    purpose: input.purpose ?? 'LOGIN',
  });
}

/**
 * Returns either a token pair or a 2FA challenge — the server decides, and the
 * login screen branches on `twoFactorRequired`. See `isTwoFactorChallenge`.
 */
export function verifyOtp(input: {
  phone: string;
  code: string;
  purpose?: OtpPurpose;
}): Promise<VerifyOtpResult> {
  return apiPost<VerifyOtpResult>('/auth/otp/verify', {
    phone: input.phone,
    code: input.code,
    purpose: input.purpose ?? 'LOGIN',
  });
}

export function completeTwoFactor(input: {
  challengeToken: string;
  code: string;
}): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/two-factor/challenge', input);
}

export function fetchMe(signal?: AbortSignal): Promise<MeResult> {
  return apiGet<MeResult>('/auth/me', undefined, signal);
}

/**
 * Mints a token scoped to another clinic this account is a member of. The
 * device session survives; only its active clinic changes, so later refreshes
 * stay in the new clinic too.
 */
export function switchClinic(clinicId: string): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/switch-clinic', { clinicId });
}

export function logout(refreshToken?: string): Promise<void> {
  return apiPost<void>('/auth/logout', refreshToken ? { refreshToken } : {});
}

export function listDeviceSessions(signal?: AbortSignal): Promise<DeviceSession[]> {
  return apiGet<DeviceSession[]>('/auth/sessions', undefined, signal);
}

export function revokeDeviceSession(id: string): Promise<void> {
  return apiDelete<void>(`/auth/sessions/${encodeURIComponent(id)}`);
}

export function logoutEverywhere(keepCurrentDevice = true): Promise<{ revoked: number }> {
  return apiDelete<{ revoked: number }>('/auth/sessions', { keepCurrentDevice });
}

/** Re-proves the second factor for an export the backend gates on step-up. */
export function twoFactorStepUp(code: string): Promise<{ validForSec: number }> {
  return apiPost<{ validForSec: number }>('/auth/two-factor/step-up', { code });
}

/** `GET /v1/security/support-access` — every Bookora support login (`audit.view`). */
export function listSupportAccess(
  page: number,
  signal?: AbortSignal,
): Promise<Paged<SupportAccessRow>> {
  return apiGet<Paged<SupportAccessRow>>('/security/support-access', { page }, signal);
}
